import { supabase } from "../lib/supabase";

const TRANSPORT_TYPE_SEA = 2;
const BOOKING_TYPE_FCL = 2;

export type FclOriginThcOption = {
  containerSizeId: number;
  containerSizeName: string;
  /** Gross template amount (includes export clearance). */
  amount: number;
};

type PricingComponentRow = {
  id: number;
  name?: string | null;
};

type TemplateComponentRow = {
  component_id: number;
  value: number | null;
  is_active: boolean | null;
  pricing_components?: PricingComponentRow | PricingComponentRow[] | null;
};

type PricingTemplateRow = {
  id: number;
  name?: string | null;
  transport_type: number | null;
  booking_type: number | null;
  incoterm: number | null;
  container_size: number | null;
  valid_from: string | null;
  valid_to: string | null;
  is_active: boolean | null;
  inherit_from_template_id: number | null;
  pricing_template_components?: TemplateComponentRow[] | null;
};

function componentName(tc: TemplateComponentRow): string {
  const pc = tc.pricing_components;
  const row = Array.isArray(pc) ? pc[0] : pc;
  return (row?.name ?? "").toLowerCase();
}

/** "Terminal handling plaats van oorsprong" */
export function isOriginTerminalHandlingName(name: string | null | undefined): boolean {
  if (!name) return false;
  const n = name.toLowerCase();
  return n.includes("terminal handling") && n.includes("oorsprong");
}

function isTemplateValidOnDate(template: PricingTemplateRow, matchDate: string): boolean {
  if (template.valid_from && template.valid_from > matchDate) return false;
  if (template.valid_to && template.valid_to < matchDate) return false;
  return true;
}

function mergeTemplateComponents(
  template: PricingTemplateRow,
  byId: Map<number, PricingTemplateRow>
): TemplateComponentRow[] {
  const merged = new Map<number, TemplateComponentRow>();

  const parentId = template.inherit_from_template_id;
  if (parentId != null) {
    const parent = byId.get(parentId);
    if (parent?.pricing_template_components) {
      for (const tc of parent.pricing_template_components) {
        if (!tc.is_active || tc.value === null) continue;
        merged.set(tc.component_id, tc);
      }
    }
  }

  if (template.pricing_template_components) {
    for (const tc of template.pricing_template_components) {
      if (!tc.is_active || tc.value === null) continue;
      merged.set(tc.component_id, tc);
    }
  }

  return [...merged.values()];
}

function originThcFromComponents(components: TemplateComponentRow[]): number | null {
  for (const tc of components) {
    if (!isOriginTerminalHandlingName(componentName(tc))) continue;
    if (typeof tc.value === "number" && tc.value > 0) return tc.value;
  }
  return null;
}

/**
 * Load FCL origin THC amounts per container size from sea+FCL pricing templates
 * (e.g. Seafreight FCL FOB 20ft / 40ft — values may live on child or inherited parent).
 *
 * Prefers a template matching `preferredIncotermId` when several match the same size.
 */
export async function fetchFclOriginThcOptions(args: {
  matchDate: string;
  preferredIncotermId?: number | null;
}): Promise<FclOriginThcOption[]> {
  const { matchDate, preferredIncotermId = null } = args;

  const { data: templates, error } = await supabase
    .from("pricing_templates")
    .select(
      `
      id,
      name,
      transport_type,
      booking_type,
      incoterm,
      container_size,
      valid_from,
      valid_to,
      is_active,
      inherit_from_template_id,
      pricing_template_components (
        component_id,
        value,
        is_active,
        pricing_components ( id, name )
      )
    `
    )
    .eq("is_active", true)
    .eq("transport_type", TRANSPORT_TYPE_SEA)
    .eq("booking_type", BOOKING_TYPE_FCL);

  if (error || !templates) {
    console.error("fetchFclOriginThcOptions: failed to load templates", error);
    return [];
  }

  const rows = templates as PricingTemplateRow[];
  const byId = new Map(rows.map((t) => [t.id, t]));

  const sizeIds = [
    ...new Set(
      rows
        .map((t) => t.container_size)
        .filter((id): id is number => typeof id === "number")
    ),
  ];

  const sizeNameById: Record<number, string> = {};
  if (sizeIds.length > 0) {
    const { data: sizeRows } = await supabase
      .from("container_size")
      .select("id, name")
      .in("id", sizeIds);
    if (sizeRows) {
      for (const row of sizeRows) {
        sizeNameById[row.id] = row.name;
      }
    }
  }

  type Candidate = { amount: number; prefersIncoterm: boolean; templateId: number };
  const bestBySize = new Map<number, Candidate>();

  for (const template of rows) {
    if (!isTemplateValidOnDate(template, matchDate)) continue;
    if (template.container_size == null) continue;

    const amount = originThcFromComponents(mergeTemplateComponents(template, byId));
    if (amount == null) continue;

    const sizeId = template.container_size;
    const prefersIncoterm =
      preferredIncotermId != null && template.incoterm === preferredIncotermId;
    const existing = bestBySize.get(sizeId);

    if (!existing) {
      bestBySize.set(sizeId, { amount, prefersIncoterm, templateId: template.id });
      continue;
    }

    // Prefer incoterm match; otherwise keep first found (stable).
    if (prefersIncoterm && !existing.prefersIncoterm) {
      bestBySize.set(sizeId, { amount, prefersIncoterm, templateId: template.id });
    }
  }

  return [...bestBySize.entries()]
    .map(([containerSizeId, cand]) => ({
      containerSizeId,
      containerSizeName: sizeNameById[containerSizeId] ?? `Size ${containerSizeId}`,
      amount: Math.round(cand.amount * 100) / 100,
    }))
    .sort((a, b) => a.containerSizeName.localeCompare(b.containerSizeName));
}

export function pickOriginThcForContainerSize(
  options: FclOriginThcOption[],
  containerSizeId: number | null | undefined
): number | null {
  if (containerSizeId == null) {
    if (options.length === 1) return options[0].amount;
    return null;
  }
  return options.find((o) => o.containerSizeId === containerSizeId)?.amount ?? null;
}
